import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  Tooltip,
  Divider,
} from '@mui/material';
import {
  History,
  Restore,
  Compare,
  Save,
  Check,
  Schedule,
} from '@mui/icons-material';
import { apiService } from '../../services/api';

/**
 * VersionHistory Component
 *
 * Displays version history for a transcript with actions:
 * - Create manual version snapshot
 * - Revert to previous version
 * - Compare versions
 * - View version details
 */
const VersionHistory = ({ transcriptId, onVersionChange, onCompare }) => {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [createVersionDialogOpen, setCreateVersionDialogOpen] = useState(false);
  const [versionComment, setVersionComment] = useState('');
  const [creating, setCreating] = useState(false);
  const [reverting, setReverting] = useState(null); // version ID being reverted to
  const [compareMode, setCompareMode] = useState(false);
  const [selectedVersions, setSelectedVersions] = useState([]);

  useEffect(() => {
    loadVersions();
  }, [transcriptId]);

  const loadVersions = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiService.getTranscriptVersions(transcriptId);
      setVersions(data);
    } catch (err) {
      setError('Failed to load version history: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateVersion = async () => {
    try {
      setCreating(true);
      await apiService.createManualVersion(transcriptId, versionComment || null);
      setCreateVersionDialogOpen(false);
      setVersionComment('');
      await loadVersions();
      if (onVersionChange) {
        onVersionChange();
      }
    } catch (err) {
      setError('Failed to create version: ' + err.message);
    } finally {
      setCreating(false);
    }
  };

  const handleRevert = async (versionId) => {
    if (!window.confirm('Are you sure you want to revert to this version? This will create a new version with the content from the selected version.')) {
      return;
    }

    try {
      setReverting(versionId);
      await apiService.revertToVersion(transcriptId, versionId);
      await loadVersions();
      if (onVersionChange) {
        onVersionChange();
      }
    } catch (err) {
      setError('Failed to revert to version: ' + err.message);
    } finally {
      setReverting(null);
    }
  };

  const handleCompareToggle = (versionId) => {
    if (selectedVersions.includes(versionId)) {
      setSelectedVersions(selectedVersions.filter(id => id !== versionId));
    } else if (selectedVersions.length < 2) {
      setSelectedVersions([...selectedVersions, versionId]);
    } else {
      // Replace the first selected version
      setSelectedVersions([selectedVersions[1], versionId]);
    }
  };

  const handleCompare = () => {
    if (selectedVersions.length === 2 && onCompare) {
      onCompare(selectedVersions[0], selectedVersions[1]);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const getVersionLabel = (version) => {
    if (version.comment && version.comment.includes('Reverted to version')) {
      return version.comment;
    }
    return version.comment || `Version ${version.version}`;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper elevation={2} sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <History />
          <Typography variant="h6">Version History</Typography>
          <Chip label={`${versions.length} versions`} size="small" />
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {compareMode && (
            <Button
              variant="contained"
              size="small"
              startIcon={<Compare />}
              onClick={handleCompare}
              disabled={selectedVersions.length !== 2}
            >
              Compare Selected ({selectedVersions.length}/2)
            </Button>
          )}
          <Button
            variant="outlined"
            size="small"
            onClick={() => setCompareMode(!compareMode)}
          >
            {compareMode ? 'Cancel Compare' : 'Compare Versions'}
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<Save />}
            onClick={() => setCreateVersionDialogOpen(true)}
          >
            Create Snapshot
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <List sx={{ maxHeight: 400, overflow: 'auto' }}>
        {versions.map((version) => (
          <React.Fragment key={version.id}>
            <ListItem
              sx={{
                bgcolor: version.is_latest ? 'action.selected' : 'transparent',
                '&:hover': { bgcolor: 'action.hover' },
                cursor: compareMode ? 'pointer' : 'default',
                border: selectedVersions.includes(version.id) ? '2px solid primary.main' : 'none',
              }}
              onClick={() => compareMode && handleCompareToggle(version.id)}
            >
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body1" fontWeight={version.is_latest ? 'bold' : 'normal'}>
                      {getVersionLabel(version)}
                    </Typography>
                    {version.is_latest && (
                      <Chip label="Current" size="small" color="primary" icon={<Check />} />
                    )}
                  </Box>
                }
                secondary={
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Schedule sx={{ fontSize: 16 }} />
                      <Typography variant="caption">
                        {formatDate(version.created_at)}
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      Provider: {version.llm_provider}
                      {version.llm_model && ` (${version.llm_model})`}
                    </Typography>
                  </Box>
                }
              />
              {!compareMode && (
                <ListItemSecondaryAction>
                  {!version.is_latest && (
                    <Tooltip title="Revert to this version">
                      <span>
                        <IconButton
                          edge="end"
                          aria-label="revert"
                          onClick={() => handleRevert(version.id)}
                          disabled={reverting === version.id}
                          size="small"
                        >
                          {reverting === version.id ? (
                            <CircularProgress size={20} />
                          ) : (
                            <Restore />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                </ListItemSecondaryAction>
              )}
            </ListItem>
            <Divider />
          </React.Fragment>
        ))}
      </List>

      {versions.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
          No version history available.
        </Typography>
      )}

      {/* Create Version Dialog */}
      <Dialog
        open={createVersionDialogOpen}
        onClose={() => !creating && setCreateVersionDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create Version Snapshot</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Create a manual snapshot of the current transcript. This is useful before making major edits.
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Version Comment (optional)"
            placeholder="e.g., Before speaker identification cleanup"
            fullWidth
            multiline
            rows={3}
            value={versionComment}
            onChange={(e) => setVersionComment(e.target.value)}
            disabled={creating}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateVersionDialogOpen(false)} disabled={creating}>
            Cancel
          </Button>
          <Button
            onClick={handleCreateVersion}
            variant="contained"
            disabled={creating}
            startIcon={creating ? <CircularProgress size={16} /> : <Save />}
          >
            Create Snapshot
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default VersionHistory;
